// Figma reference https://www.figma.com/file/KYVPfofjfGxkFtlbcEb6vx/00---Color?node-id=11%3A20
import legacyPalette from 'components/Legacy/palette'
import darken from 'polished/lib/color/darken'
import transparentize from 'polished/lib/color/transparentize'

const blues = {
  blue000: '#ECF5FE',
  blue100: '#D8EBFD',
  blue200: '#BBDBFC',
  blue300: '#85B5F8',
  blue400: '#619FF7',
  blue500: '#3D89F5',
  blue600: '#0C6CF2',
  blue700: '#1656B9',
  blue800: '#144699',
  blue900: '#0D3578'
}

const darkmodes = {
  dark000: '#D2D4D6',
  dark100: '#B8B9BD',
  dark200: '#989BA1',
  dark300: '#797D84',
  dark400: '#63676F',
  dark500: '#4D515B',
  dark600: '#3B3E46',
  dark700: '#2C3038',
  dark800: '#20242C',
  dark900: '#0E121B'
}

const greens = {
  green000: '#E6F5EB',
  green100: '#D1F0DB',
  green200: '#AEE6CA',
  green300: '#8BDCB3',
  green400: '#17CE73',
  green500: '#339F7B',
  green600: '#00994C',
  green700: '#006633',
  green800: '#004C26',
  green900: '#003319'
}

const greys = {
  grey000: '#F0F2F7',
  grey100: '#DFE3EB',
  grey200: '#CCD2DE',
  grey300: '#B1B8C7',
  grey400: '#98A1B2',
  grey500: '#828B9E',
  grey600: '#677184',
  grey700: '#50596B',
  grey800: '#353F52',
  grey900: '#121D33'
}

const reds = {
  red000: '#FFECEB',
  red100: '#FFD9D6',
  red200: '#FFC3BF',
  red300: '#F6A7A1',
  red400: '#FF3344',
  red500: '#EA1F30',
  red600: '#CF1726',
  red700: '#A50D0D',
  red800: '#8D0B0B',
  red900: '#790606'
}

const oranges = {
  orange000: '#FFF2E5',
  orange100: '#FAE4CD',
  orange200: '#FFD6AD',
  orange300: '#FFC994',
  orange400: '#FFB266',
  orange500: '#F5A250',
  orange600: '#F28B24',
  orange700: '#D96716',
  orange800: '#B24400',
  orange900: '#8C2F00'
}

const purples = {
  purple000: '#EFECFE',
  purple100: '#DED8FD',
  purple200: '#C6BBFC',
  purple300: '#ADA6FF',
  purple400: '#9080FF',
  purple500: '#7349F2',
  purple600: '#5322E5',
  purple700: '#451DBF',
  purple800: '#371799',
  purple900: '#250B73'
}

const teals = {
  teal000: '#E6F8FA',
  teal100: '#D1EDF0',
  teal200: '#AEE1E6',
  teal300: '#6CD0D9',
  teal400: '#00B6C7',
  teal500: '#12A5B2',
  teal600: '#0E828C',
  teal700: '#055E66',
  teal800: '#08474C',
  teal900: '#052F33'
}

const overlays = {
  overlayAltDark800: 'rgba(14, 18, 27, 0.8)',
  overlayDark100: 'rgba(5, 24, 61, 0.1)',
  overlayDark200: 'rgba(5, 24, 61, 0.2)',
  overlayDark400: 'rgba(5, 24, 61, 0.4)',
  overlayDark600: 'rgba(5, 24, 61, 0.6)',
  overlayDark800: 'rgba(5, 24, 61, 0.8)',
  overlayWhite100: 'rgba(255, 255, 255, 0.1)',
  overlayWhite200: 'rgba(255, 255, 255, 0.2)',
  overlayWhite400: 'rgba(255, 255, 255, 0.4)',
  overlayWhite600: 'rgba(255, 255, 255, 0.6)',
  overlayWhite800: 'rgba(255, 255, 255, 0.8)'
}

const base = {
  black: '#000000',
  body: '#677184',
  dark: greys.grey300,
  error: reds.red600,
  errorMuted: '#FF3344',
  exchange: '#0E828C',
  exchangeMuted: '#00B6C7',
  explorer: '#5322E5',
  explorerMuted: '#9080FF',
  light: '#F0F2F7',
  medium: greys.grey100,
  muted: '#98A1B2',
  primary: blues.blue600,
  primaryMuted: blues.blue400,
  success: greens.green600,
  successMuted: '#17CE73',
  title: '#121D33',
  warning: '#D46A00',
  warningMuted: '#FFB266',
  white: '#FFFFFF'
}

const tiers = {
  tierDiamond: '#B3CCE5',
  tierGold: '#F5B73D',
  tierSilver: '#C2C9D6'
}

const exchange = {
  exchangeAqua: '#4AE5FF',
  exchangeIndigo: '#2219CC',
  exchangeNight: '#0A0D10',
  exchangePink: '#CC0088',
  exchangeTurquoise: '#42F1B8'
}

const crypto = {
  algo: '#000000',
  algoBackground: 'rgba(0, 0, 0, 0.15)',
  bch: '#8DC351',
  bchBackground: 'rgba(141, 195, 81, 0.15)',
  btc: '#FF9B22',
  btcBackground: 'rgba(255, 155, 34, 0.15)',
  digitalGold: '#FFE738',
  digitalGoldBackground: 'rgba(255, 231, 56, 0.15)',
  eth: '#473BCB',
  ethBackground: 'rgba(71, 59, 203, 0.15)',
  fiat: '#00B26B',
  fiatBackground: 'rgba(0, 178, 107, 0.15)',
  ltc: '#BFBBBB',
  ltcBackground: 'rgba(191, 187, 187, 0.15)',
  pax: greens.green700,
  paxBackground: 'rgba(38, 161, 123, 0.15)',
  stx: '#211F6D',
  stxBackground: 'rgba(33, 31, 109, 0.15)',
  usdd: '#00522C',
  usddBackground: 'rgba(0, 82, 44, 0.15)',
  usdt: '#26A17B',
  usdtBackground: 'rgba(38, 161, 123, 0.15)',
  xlm: '#000000',
  xlmBackground: 'rgba(0, 0, 0, 0.15)',
  xrp: '#000000',
  xrpBackground: 'rgba(0, 0, 0, 0.15)'
}

// not in figma but useful
const social = {
  facebook: '#227BEE',
  twitter: '#00ACED'
}

const legacy = {
  darkCerulean: darken(0.1, blues.blue600),
  darkGold: darken(0.1, crypto.digitalGold),
  darkOrient: darken(0.1, legacyPalette.orient),
  darkPurple: darken(0.1, purples.purple700),
  transparentGreen: transparentize(0.7, greens.green600),
  transparentRed: transparentize(0.7, reds.red600)
}

const custom = {
  cancelActive: 'rgba(207, 23, 38, 0.5)',
  cancelBackground: 'rgba(207, 23, 38, 0.25)',
  cancelHover: 'rgba(207, 23, 38, 0.35)',
  notVerifiedBackground: 'rgba(255, 51, 68, 0.17)',
  ukFinPromBackground: '#65a5ff',
  ukFinPromBannerBackground: '#ffd9d6',
  ukFinPromText: '#07080D',
  verifiedBackground: 'rgba(23, 206, 115, 0.25)'
}

const palette = {
  ...legacyPalette,
  ...legacy,
  ...base,
  ...blues,
  ...crypto,
  ...custom,
  ...darkmodes,
  ...exchange,
  ...greens,
  ...greys,
  ...oranges,
  ...overlays,
  ...purples,
  ...reds,
  ...social,
  ...teals,
  ...tiers
}

export default palette
